import {inject, injectable} from 'inversify';

import type {SubgoalApiModel} from '../../../../api/goal';
import type {DateTimeObj} from '../../../../utils/datetime';
import type {Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {DateTimeModel} from '../../Date/DateTime.model';

import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {SubgoalFactory} from '../Subgoal.factory';
import {SubgoalModel} from '../Subgoal.model';
import {SubgoalExpandJrpc} from './Subgoal.expand.jrpc';

@injectable()
export class SubgoalMapperJrpc
  implements
    IApiDataMapper<SubgoalMapperJrpc.ApiData, SubgoalModel.Props, SubgoalModel>
{
  constructor(
    @inject(SubgoalFactory)
    private readonly factory: SubgoalFactory,
    @inject(SubgoalExpandJrpc)
    public readonly expand: SubgoalExpandJrpc,
    @inject(DateTimeModelFactory)
    public readonly dateTimeFactory: DateTimeModelFactory,
  ) {}

  mapDataToModel(apiData: SubgoalMapperJrpc.ApiData): SubgoalModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(data: SubgoalMapperJrpc.ApiData): SubgoalModel.Props {
    return {
      // We need apiData here, because we don't have edit subgoal modal
      // to work with SubgoalModel (only with SubgoalApiModel)
      // TODO: remove it when there is a component for editing SubgoalModel
      apiData: data,
      checkpoints: this.mapCheckpoints(data.checkpoints),
      clientAttitude: data.client_attitude,
      clientComment: data.client_comment,
      clientId: data.client_id,
      coachAttitude: data.coach_attitude,
      coachComment: data.coach_comment,
      coachIds: data.coach_ids,
      color: data.color,
      counters: {
        childSubgoals: data.counters.child_subgoals,
        clientsNeedReview: data.counters.clients_need_review,
        clientsReached: data.counters.clients_reached,
      },
      courseId: data.course_id,
      created: this.dateTimeFactory.createFromDateTimeObject(data.created),
      currentValue: data.current_value,
      description: data.description,
      endDate: this.mapOptionalDate(data.end_date),
      goalId: data.goal_id,
      iconName: data.icon,
      id: data._id,
      initialValue: data.initial_value,
      isClientManaged: data.is_client_managed,
      lastActivity: this.mapOptionalDate(data.last_activity),
      parentSubgoalId: data.parent_subgoal_id,
      programId: data.program_id,
      progress: data.progress,
      reached: this.mapOptionalDate(data.reached),
      reviewUntil: this.mapOptionalDate(data.review_until),
      sortOrder: data.sort_order,
      startDate: this.mapOptionalDate(data.start_date),
      status: data.status,
      targetValue: data.target_value,
      title: data.title,
      unit: data.unit,
      updated: this.dateTimeFactory.createFromDateTimeObject(data.updated),
      userId: data.user_id,
    };
  }

  private mapCheckpoints(
    checkpoints: SubgoalApiModel['checkpoints'],
  ): SubgoalModel.Checkpoint[] {
    return checkpoints.map(({reached, title, uuid}) => ({
      reached: this.mapOptionalDate(reached),
      title,
      uuid,
    }));
  }

  private mapOptionalDate(
    dateTimeObj: Nullable<DateTimeObj>,
  ): DateTimeModel | null {
    return dateTimeObj
      ? this.dateTimeFactory.createFromDateTimeObject(dateTimeObj)
      : null;
  }
}

export namespace SubgoalMapperJrpc {
  export type ApiData = SubgoalApiModel;

  export type ExpandedApiData = ApiData & SubgoalExpandJrpc.ExpandedFields;
}
